import { useMemo } from 'react';

export type Storefront = 'ch' | 'de' | 'at' | 'it' | 'se' | 'al';
export type Currency = 'CHF' | 'EUR' | 'SEK' | 'ALL';

/**
 * Dedicated render-host alias for the AT storefront. Points at the same
 * published build as bigfollow.lovable.app, but is hard-wired to
 * storefront='at' so the external prerender service can fetch AT snapshots
 * without any query-param trickery. Matched by the wildcard
 * `host.endsWith('.bigfollow.at')` below — listed explicitly so the intent
 * is documented and the wildcard is not accidentally narrowed later.
 * DO NOT emit noindex on this host: snapshots are served 1:1 for
 * bigfollow.at; the canonical (rewritten to https://bigfollow.at) is what
 * prevents duplicate indexing.
 */
export const AT_RENDER_HOST = 'render-at.bigfollow.at';

/**
 * Dedicated render-host alias for the IT storefront. Mirrors AT_RENDER_HOST.
 * DNS is not yet delegated; the constant is code-inert until 7g cutover.
 * Matched by the wildcard `host.endsWith('.bigfollow.it')` below — listed
 * explicitly so the intent is documented.
 */
export const IT_RENDER_HOST = 'render-it.bigfollow.it';

/**
 * Dedicated render-host alias for the AL storefront. Mirrors AT/IT render
 * hosts. DNS is not delegated yet; the constant is code-inert until the AL
 * cutover. Matched by the wildcard `host.endsWith('.bigfollow.al')` below.
 */
export const AL_RENDER_HOST = 'render-al.bigfollow.al';
// Duplicated literal (not imported from useLanguage) to avoid a circular dep:
// useLanguage may depend on useStorefront, never the reverse.
type StorefrontLocale = 'de' | 'en' | 'it' | 'fr' | 'sv' | 'sq';

export interface StorefrontConfig {
  storefront: Storefront;
  currency: Currency;
  currencySymbol: string;
  flag: string;
  countryCode: 'CH' | 'DE' | 'AT' | 'IT' | 'SE' | 'AL';
  domain: string;
  defaultLocale: StorefrontLocale;
}

/**
 * Detects the active storefront based on window.location.hostname.
 * - bigfollow.de / *.bigfollow.de → DE (EUR, de)
 * - bigfollow.at / *.bigfollow.at → AT (EUR, de)
 * - bigfollow.it / *.bigfollow.it → IT (EUR, it)
 * - bigfollow.se / *.bigfollow.se → SE (SEK, sv)
 * - bigfollow.al / *.bigfollow.al → AL (EUR, sq)  -- ALL/Lek later without rebuild
 * - everything else (localhost, lovable.app, bigfollow.ch) → CH (CHF, de)
 *
 * SSR-safe: returns 'ch' when window is undefined.
 */
export function detectStorefront(hostname?: string): Storefront {
  // P2.6 §5 — highest-priority override written by the AT boot-script in
  // index.html BEFORE the module bundle runs. Only set when the real host is
  // bigfollow.at (or *.bigfollow.at). Never set on bigfollow.ch or previews,
  // so CH remains byte-identical and the ?sf=at preview flag stays the sole
  // AT trigger on preview hosts.
  if (typeof window !== 'undefined') {
    const forced = (window as any).__BF_FORCE_SF__;
    if (forced === 'at' || forced === 'ch' || forced === 'it' || forced === 'al') return forced as Storefront;
  }
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  if (!host) return 'ch';
  if (host === 'bigfollow.de' || host.endsWith('.bigfollow.de')) return 'de';
  // Includes AT_RENDER_HOST (render-at.bigfollow.at) via the wildcard suffix.
  if (host === 'bigfollow.at' || host.endsWith('.bigfollow.at')) return 'at';
  // Includes IT_RENDER_HOST (render-it.bigfollow.it) via wildcard suffix.
  if (host === 'bigfollow.it' || host.endsWith('.bigfollow.it')) return 'it';
  if (host === 'bigfollow.se' || host.endsWith('.bigfollow.se')) return 'se';
  if (host === 'bigfollow.al' || host.endsWith('.bigfollow.al')) return 'al';
  // P2.6 §4: AT preview-only override. Activated ONLY when
  //   1. VITE_PREVIEW_ENABLE_AT === 'true' (never set on prod build), AND
  //   2. host is a preview host (localhost / *.lovable.app / *.lovable.dev), AND
  //   3. session opted in via ?sf=at (persisted to sessionStorage 'preview_sf').
  // The prod domain bigfollow.ch NEVER matches the host guard, so the flag is a no-op there.
  // Preview override supports 'at' and 'it' (IT added for preview testing of the
  // IT storefront). Prod hosts never match the guard, so CH stays untouched.
  const previewSf = isPreviewAtEnabled(host) ? readPreviewSf() : null;
  if (previewSf === 'at' || previewSf === 'it' || previewSf === 'al') return previewSf;
  return 'ch';
}

function isPreviewAtEnabled(host: string): boolean {
  try {
    // Vite replaces `import.meta.env.VITE_*` at build/serve time via textual match
    // (optional chaining is NOT rewritten — must be dot access).
    const flag = import.meta.env.VITE_PREVIEW_ENABLE_AT === 'true';
    if (!flag) return false;
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.lovable.app') ||
      host.endsWith('.lovable.dev')
    );
  } catch {
    return false;
  }
}

function readPreviewSf(): Storefront | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    // `__bf_sf` is the explicit preview flag; `sf` stays supported (AT/IT legacy).
    const q = url.searchParams.get('__bf_sf') ?? url.searchParams.get('sf');
    if (q === 'at' || q === 'ch' || q === 'it' || q === 'al') {
      window.sessionStorage.setItem('preview_sf', q);
      return q as Storefront;
    }
    const stored = window.sessionStorage.getItem('preview_sf');
    if (stored === 'at' || stored === 'ch' || stored === 'it' || stored === 'al') return stored as Storefront;
  } catch {}
  return null;
}

const CONFIG: Record<Storefront, StorefrontConfig> = {
  ch: {
    storefront: 'ch',
    currency: 'CHF',
    currencySymbol: 'CHF',
    flag: '🇨🇭',
    countryCode: 'CH',
    domain: 'bigfollow.ch',
    defaultLocale: 'de',
  },
  de: {
    storefront: 'de',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇩🇪',
    countryCode: 'DE',
    domain: 'bigfollow.de',
    defaultLocale: 'de',
  },
  at: {
    storefront: 'at',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇦🇹',
    countryCode: 'AT',
    domain: 'bigfollow.at',
    defaultLocale: 'de',
  },
  it: {
    storefront: 'it',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇮🇹',
    countryCode: 'IT',
    domain: 'bigfollow.it',
    defaultLocale: 'it',
  },
  se: {
    storefront: 'se',
    currency: 'SEK',
    currencySymbol: 'kr',
    flag: '🇸🇪',
    countryCode: 'SE',
    domain: 'bigfollow.se',
    defaultLocale: 'sv',
  },
  al: {
    // Currency is EUR for launch; switch to 'ALL' + symbol 'L' later without any structural rebuild.
    storefront: 'al',
    currency: 'EUR',
    currencySymbol: '€',
    flag: '🇦🇱',
    countryCode: 'AL',
    domain: 'bigfollow.al',
    defaultLocale: 'sq',
  },
};

/** All defined storefronts (for iteration, e.g. hreflang). */
export const ALL_STOREFRONTS: Storefront[] = ['ch', 'de', 'at', 'it', 'se', 'al'];

/**
 * Storefronts that are publicly live. New domains stay code-prepared but
 * invisible in UI/sitemap until explicitly promoted to LIVE.
 */
export const LIVE_STOREFRONTS: Storefront[] = ['ch', 'at', 'it', 'al'];

/**
 * Storefront -> display currency. Single source of truth.
 *
 * KEEP IN SYNC (SSOT) with:
 *   - CONFIG[...].currency below (same values)
 *   - Trigger `public.pvp_validate_currency()` in the DB (product_variation_prices)
 *   - `formatPriceForCurrency` in src/lib/formatPrice.ts (must handle every currency here)
 *   - `roundPrice` strategies in src/lib/priceRounding.ts
 *
 * When you change a mapping (e.g. AL: EUR → ALL), update ALL of the above in
 * the same change or displayed prices and stored overrides will drift.
 */
export const STOREFRONT_CURRENCY: Record<Storefront, Currency> = {
  ch: 'CHF',
  de: 'EUR',
  at: 'EUR',
  it: 'EUR',
  se: 'SEK',
  al: 'EUR', // NOTE: AL uses EUR at launch. Trigger mirrors this.
};

/**
 * Storefronts on which the Big-Punkte loyalty programme is switched OFF.
 *
 * The programme is structurally CHF-denominated (points_per_chf,
 * points_per_chf_redemption, CHF redemption value). Until a loyalty currency
 * layer ships, every non-CHF storefront is gated so customers never see
 * CHF-based point values.
 *
 * LIST-DRIVEN ON PURPOSE: adding a future storefront (de/se/...) is a
 * one-line change here — no per-case `=== 'it'` checks anywhere else.
 * Consumers: useLoyalty (config + overview), the /big-punkte route gate,
 * the account Loyalty tab, and the loyalty-thankyou mail runner
 * (mirrored list in supabase/functions/loyalty-thankyou-cron|email).
 */
export const LOYALTY_DISABLED_STOREFRONTS: Storefront[] = ['it', 'al'];

/**
 * Storefronts whose social-proof order counts must be scoped to THEIR OWN
 * orders instead of the global (all-storefront) count.
 *
 * Currently empty: AL is a fresh storefront with zero own orders, which hid
 * the "Zuletzt bestellt"-Zeile completely. AL therefore uses the global count
 * (with AL city pool + sq time labels), exactly like CH/AT/IT.
 * LIST-DRIVEN: add a storefront here to scope its counts.
 */
export const STOREFRONT_SCOPED_ORDER_COUNTS: Storefront[] = [];

/** Storefront value to scope order counts on, or undefined for global counts. */
export function orderCountScope(sf: Storefront): string | undefined {
  return STOREFRONT_SCOPED_ORDER_COUNTS.includes(sf) ? sf : undefined;
}

/**
 * Storefronts whose product URLs live on the ROOT (/instagram-follower)
 * instead of behind the /produkt/ prefix. Legacy Woo URL structure on AL.
 */
export const ROOT_PRODUCT_STOREFRONTS: Storefront[] = ['al'];

/**
 * Storefronts that serve EXACTLY ONE language at the URL root and must never
 * expose locale-prefixed routes (/de, /en, /fr, /it) nor a language switcher.
 *
 * LIST-DRIVEN ON PURPOSE (analog LOYALTY_DISABLED_STOREFRONTS): route
 * mounting (App.tsx), the language switcher (Header/MobileMenu) and the
 * sitemap/hreflang generation all read from here, so storefront #5 is
 * automatically correct. CH stays the only multi-locale storefront.
 */
export const SINGLE_LANGUAGE_STOREFRONTS: Storefront[] = ['at', 'de', 'it', 'se', 'al'];

/** True when the active storefront must not show locale routes/switcher. */
export function isSingleLanguage(sf?: Storefront): boolean {
  try {
    return SINGLE_LANGUAGE_STOREFRONTS.includes(sf ?? detectStorefront());
  } catch {
    return false;
  }
}

/**
 * Storefronts with STRICT route aliasing: only the segments of their own root
 * locale are mounted. Fremdsprachige Alias-Segmente (/produkt/, /prodotto/,
 * /kontakt, /anmelden, …) laufen dort ins 404, damit unter der Domain keine
 * Duplikate anderer Storefronts erreichbar sind.
 *
 * CH ist mehrsprachig und daher NICHT enthalten (de/en/it/fr sind dort
 * legitim). AT/IT/AL/SE sind Ein-Sprach-Storefronts: dort ist jedes fremde
 * Segment ein Duplikat einer anderen Domain und muss 404 liefern.
 * (IT aufgenommen am 2026-08-19: bigfollow.it/produkt/… lieferte live die
 * komplette CH-Seite ohne Canonical — bewusste, sichtbare Korrektur.)
 */
export const STRICT_LOCALE_ROUTING_STOREFRONTS: Storefront[] = ['at', 'de', 'it', 'se', 'al'];

/** True when only root-locale route segments may be mounted. */
export function isStrictLocaleRouting(sf?: Storefront): boolean {
  try {
    return STRICT_LOCALE_ROUTING_STOREFRONTS.includes(sf ?? detectStorefront());
  } catch {
    return false;
  }
}

/** True when loyalty must be completely invisible on the active storefront. */
export function isLoyaltyDisabled(sf?: Storefront): boolean {
  try {
    return LOYALTY_DISABLED_STOREFRONTS.includes(sf ?? detectStorefront());
  } catch {
    return false;
  }
}

/**
 * Locale of the blog/content corpus a storefront is allowed to show.
 * CH/AT keep their historical behaviour (German corpus, no extra filter is
 * applied by callers), IT shows Italian, AL shows Albanian.
 */
export const STOREFRONT_POST_LOCALE: Record<Storefront, string> = {
  ch: 'de',
  de: 'de',
  at: 'de',
  it: 'it',
  se: 'de',
  al: 'sq',
};

/**
 * Storefronts whose content corpus is locale-exclusive: listings, search,
 * sitemaps and the slug resolver MUST filter on STOREFRONT_POST_LOCALE.
 * CH/AT are deliberately absent so their queries stay byte-identical.
 */
export const LOCALE_EXCLUSIVE_STOREFRONTS: Storefront[] = ['it', 'al'];

/** Post locale to filter on, or null when the storefront must not be filtered. */
export function exclusivePostLocale(sf?: Storefront): string | null {
  const active = sf ?? detectStorefront();
  return LOCALE_EXCLUSIVE_STOREFRONTS.includes(active) ? STOREFRONT_POST_LOCALE[active] : null;
}

/**
 * Returns the canonical https base URL to use for SEO tags (canonical, og:url,
 * hreflang, JSON-LD). Non-live storefronts fall back to the CH domain so that
 * dormant domains can never leak fake canonicals or split ranking signals.
 */
export function getActiveSiteUrl(sf?: Storefront): string {
  const active = sf ?? detectStorefront();
  const cfg = CONFIG[active];
  if (LIVE_STOREFRONTS.includes(active)) return `https://${cfg.domain}`;
  return 'https://bigfollow.ch';
}

export function useStorefront(): StorefrontConfig {
  return useMemo(() => CONFIG[detectStorefront()], []);
}

export function getStorefrontConfig(sf: Storefront): StorefrontConfig {
  return CONFIG[sf];
}

/**
 * Storefront-aware default e-mail placeholder for inputs.
 * Keeps CH/AT/DE byte-identical to today ("deine@email.ch") and switches
 * to a neutral Italian sample on the IT storefront.
 */
export function emailPlaceholder(sf?: Storefront): string {
  const active = sf ?? detectStorefront();
  switch (active) {
    case 'it': return 'esempio@email.it';
    case 'at': return 'deine@email.at';
    case 'se': return 'din@email.se';
    case 'al': return 'shembull@email.al';
    default:   return 'deine@email.ch';
  }
}